import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ClockCounterClockwise, EyeSlash, Eye, Fingerprint } from "@phosphor-icons/react";
import { login } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePasskey } from "@/lib/webauthn";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import MarketingHeader from "@/components/marketing/MarketingHeader";
import { ContourBackground } from "@/components/marketing/SresVisuals";
import useSEO from "@/lib/useSEO";

/**
 * Mobile-first SRES sign-in page.
 *
 * Layout:
 *   • Mobile: single column — brand block on top, login card below
 *   • Desktop: same single column, capped width, centred
 *
 * Includes a "Remember me" toggle that switches auth storage between
 * localStorage (persistent) and sessionStorage (session-only).
 */
export default function LoginPage() {
  useSEO({
    title: "Sign in",
    description:
      "Sign in to your SRES workspace — resource estimation, kriging, block modelling and Competent Person Reports.",
    path: "/login",
  });

  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login: setLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);

  const passkey = usePasskey({
    onAuthenticated: (res) => {
      setLogin(res.access_token, res.user, remember);
      toast.success(`Welcome, ${res.user.name || res.user.email}`);
      if (res.user.must_change_password) {
        nav("/change-password?first=1", { replace: true });
      } else {
        nav(sp.get("redirect") || "/app", { replace: true });
      }
    },
  });

  const submit = async (e) => {
    e.preventDefault();
    setPending(true);
    try {
      const res = await login({ email, password });
      setLogin(res.access_token, res.user, remember);
      toast.success(`Welcome, ${res.user.name || res.user.email}`);
      if (res.user.must_change_password) {
        nav("/change-password?first=1", { replace: true });
      } else {
        nav(sp.get("redirect") || "/app", { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-white flex flex-col overflow-x-hidden"
      data-testid="login-page"
    >
      <MarketingHeader />

      <main
        className="flex-1 relative w-full px-5 sm:px-6 py-10 sm:py-14 overflow-hidden"
        style={{
          background:
            "radial-gradient(900px 600px at 50% -10%, rgba(30,111,255,0.08), transparent 60%), #FFFFFF",
        }}
      >
        {/* Faint grid + contour backdrop */}
        <div
          className="absolute inset-0 grid-lines opacity-40 pointer-events-none"
          aria-hidden
        />
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-20 w-[720px] max-w-none h-[560px] opacity-[0.55] pointer-events-none"
          aria-hidden
        >
          <ContourBackground />
        </div>

        <div className="relative w-full max-w-[420px] mx-auto flex flex-col gap-6">
          {/* ── Brand block (top) ─────────────────────────────── */}
          <div
            className="text-center pt-2"
            data-testid="login-brand-block"
          >
            <div className="flex justify-center">
              <img
                src="/sres-logo-horizontal.png?v=5"
                alt="SRES · Shanvi Resource Estimation Studio"
                className="h-12 sm:h-14 w-auto object-contain max-w-full"
                draggable={false}
              />
            </div>
            <h1
              className="mt-6 font-heading font-black text-slate-900 text-3xl sm:text-4xl tracking-[-0.02em]"
              data-testid="login-heading"
            >
              Welcome back
            </h1>
            <p className="mt-2 text-slate-500 text-[14px] sm:text-[15px]">
              Sign in to your{" "}
              <span className="text-slate-700 font-medium">SRES</span> workspace.
            </p>
          </div>

          {sp.get("expired") && (
            <div
              className="flex items-start gap-3 rounded-lg border border-brand-blue/25 bg-brand-blue/5 px-4 py-3 text-left"
              data-testid="login-session-expired-banner"
              role="alert"
            >
              <ClockCounterClockwise
                size={18}
                weight="bold"
                className="text-brand-blue shrink-0 mt-0.5"
              />
              <div className="text-[13px] leading-snug">
                <div className="font-semibold text-slate-900">
                  Your session has ended
                </div>
                <div className="text-slate-600 mt-0.5">
                  For your security, you&apos;ve been signed out. Please sign in
                  again to continue.
                </div>
              </div>
            </div>
          )}

          {/* ── Login card ────────────────────────────────────── */}
          <form
            onSubmit={submit}
            className="bg-white border border-slate-200 rounded-2xl shadow-[0_20px_60px_-30px_rgba(15,23,42,0.18)] p-5 sm:p-8"
            data-testid="login-form"
          >
            <div className="space-y-5">
              <div>
                <Label
                  htmlFor="email"
                  className="text-[13px] font-semibold text-slate-800"
                >
                  Email or Login ID
                </Label>
                <Input
                  id="email"
                  type="text"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-12 bg-slate-50 border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand-blue focus-visible:border-brand-blue focus-visible:bg-white text-[16px]"
                  placeholder="you@example.com or login ID"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  data-testid="login-email"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label
                    htmlFor="password"
                    className="text-[13px] font-semibold text-slate-800"
                  >
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-[13px] font-medium text-brand-blue hover:underline"
                    data-testid="link-forgot"
                  >
                    Forgot?
                  </Link>
                </div>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 bg-slate-50 border-slate-200 rounded-md text-slate-900 placeholder:text-slate-400 focus-visible:ring-brand-blue focus-visible:border-brand-blue focus-visible:bg-white text-[16px] pr-11"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-md"
                    data-testid="login-toggle-password"
                  >
                    {showPassword ? (
                      <EyeSlash size={18} weight="regular" />
                    ) : (
                      <Eye size={18} weight="regular" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <label
                htmlFor="remember-me"
                className="flex items-center gap-3 cursor-pointer select-none"
                data-testid="login-remember-label"
              >
                <Checkbox
                  id="remember-me"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)}
                  className="h-5 w-5 border-slate-300 data-[state=checked]:bg-brand-blue data-[state=checked]:border-brand-blue"
                  data-testid="login-remember"
                />
                <span className="text-[13.5px] text-slate-700">
                  Keep me signed in on this device
                </span>
              </label>

              <Button
                type="submit"
                disabled={pending}
                className="w-full h-12 bg-slate-900 hover:bg-black text-white font-semibold rounded-md text-[15px] inline-flex items-center justify-center gap-2"
                data-testid="login-submit"
              >
                {pending ? (
                  "Signing in…"
                ) : (
                  <>
                    Sign in <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </Button>

              {/* ── Passkey sign-in ─────────────────────────────── */}
              {passkey.supported && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <div className="flex-1 h-px bg-slate-200" />
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-400">
                      or
                    </div>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <Button
                    type="button"
                    onClick={() => passkey.signIn().catch((e) => toast.error(e.message || "Passkey sign-in failed"))}
                    disabled={passkey.pending}
                    className="w-full h-12 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 hover:border-slate-900 font-semibold rounded-md text-[15px] inline-flex items-center justify-center gap-2"
                    data-testid="login-passkey-btn"
                  >
                    <Fingerprint size={18} weight="regular" />
                    {passkey.pending ? "Waiting for passkey…" : "Sign in with Passkey"}
                  </Button>
                </>
              )}
            </div>

            {/* Divider + signup */}
            <div className="mt-8 pt-6 border-t border-slate-100 text-[13.5px] text-slate-500 text-center">
              New to SRES?{" "}
              <Link
                to="/signup"
                className="text-brand-blue font-semibold hover:underline"
                data-testid="link-signup"
              >
                Create an account
              </Link>
            </div>
          </form>

          {/* Small tagline / trust cue below the card */}
          <div className="text-center text-[11.5px] text-slate-400 tracking-wide">
            Secured by SRES · info@sresources.co
          </div>
        </div>
      </main>
    </div>
  );
}
